import { useEffect, useMemo, useState } from 'react';
import {
  accessLogCsvUrl,
  accessLogSummary,
  listAccessLog,
  sendAccessSummaryNow,
  type AccessLogAction,
  type AccessLogFilters,
  type AccessLogRow,
  type AccessLogStats,
  type AccessSummaryRow,
} from '../api/admin';
import { ApiError } from '../api/client';
import { useFeather } from '../shell/useFeather';
import './activity-pane.css';

const ACTION_LABEL: Record<AccessLogAction, string> = {
  list: 'Prohlížení',
  detail: 'Detail',
  write: 'Editace',
  export: 'Export',
};

const ACTION_TITLE: Record<AccessLogAction, string> = {
  list: 'Zobrazení seznamu nebo přehledu',
  detail: 'Otevření detailu konkrétní entity',
  write: 'Vytvoření, změna nebo smazání dat',
  export: 'Stažení dat (CSV apod.)',
};

const STATUS_LABEL: Record<number, [string, string]> = {
  200: ['OK', 'Požadavek proběhl v pořádku'],
  201: ['Vytvořeno', 'Záznam byl vytvořen'],
  204: ['OK', 'Provedeno bez odpovědi'],
  302: ['Přesměrování', 'Server přesměroval na jinou adresu'],
  304: ['Beze změny', 'Data se od minula nezměnila, prohlížeč použil kopii'],
  400: ['Chybný požadavek', 'Neplatný vstup'],
  401: ['Nepřihlášen', 'Chybí platné přihlášení'],
  403: ['Zamítnuto', 'Uživatel nemá oprávnění'],
  404: ['Nenalezeno', 'Záznam neexistuje'],
  429: ['Omezeno', 'Příliš mnoho požadavků'],
  500: ['Chyba serveru', 'Interní chyba aplikace'],
};

function statusLabel(code: number): [string, string] {
  return STATUS_LABEL[code] ?? (code >= 500 ? ['Chyba serveru', `HTTP ${code}`] : code >= 400 ? ['Chyba', `HTTP ${code}`] : code >= 300 ? ['Přesměrování', `HTTP ${code}`] : ['OK', `HTTP ${code}`]);
}

const MODULE_LABEL: Record<string, string> = {
  'rondo:uzivatele': 'Rondo · Uživatelé', 'rondo:finance': 'Rondo · Finance', 'rondo:produkty': 'Rondo · Produkty',
  'rondo:aktivita': 'Rondo · Aktivita', 'rondo:marketing': 'Rondo · Marketing',
  'affilmaster:klienti': 'Affilmaster · Klienti', 'affilmaster:affiliates': 'Affilmaster · Affiliates',
  'affilmaster:finance': 'Affilmaster · Finance', 'affilmaster:mailing': 'Affilmaster · Mailing',
  'affilmaster:dashboard': 'Affilmaster · Dashboard', 'affilplatform:mailing': 'Affilplatform · Mailing', crm: 'CRM',
};

function moduleLabel(key: string): string {
  if (MODULE_LABEL[key]) return MODULE_LABEL[key];
  const [space, section] = key.split(':');
  const cap = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
  return section ? `${cap(space!)} · ${cap(section)}` : cap(space!);
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function lastMonths(count: number): { value: string; label: string }[] {
  const items: { value: string; label: string }[] = [];
  const cursor = new Date();
  cursor.setDate(1);
  for (let index = 0; index < count; index += 1) {
    const value = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`;
    const label = cursor.toLocaleDateString('cs-CZ', { month: 'long', year: 'numeric' });
    items.push({ value, label: label.charAt(0).toUpperCase() + label.slice(1) });
    cursor.setMonth(cursor.getMonth() - 1);
  }
  return items;
}

const MONTH_OPTIONS = lastMonths(12);

const NUMBER_FORMAT = new Intl.NumberFormat('cs-CZ');
const SHORT_DATE_FORMAT = new Intl.DateTimeFormat('cs-CZ', { day: 'numeric', month: 'numeric' });
const TIME_FORMAT = new Intl.DateTimeFormat('cs-CZ', { hour: '2-digit', minute: '2-digit' });
const FULL_DATE_FORMAT = new Intl.DateTimeFormat('cs-CZ', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function initials(name: string, email: string): string {
  const source = name.trim() || email.trim() || 'P1';
  const words = source.split(/\s+/).filter(Boolean);
  return ((words[0]?.[0] ?? '') + (words[1]?.[0] ?? words[0]?.[1] ?? '')).toUpperCase();
}

function visiblePath(path: string): string {
  return path.replace(/^\/api\/v1\//, '').replace(/^\//, '');
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError ? `${error.status} ${error.code}` : String(error);
}

function statusTone(status: number): 'ok' | 'redirect' | 'error' {
  if (status >= 400) return 'error';
  if (status >= 300) return 'redirect';
  return 'ok';
}

function counted(value: number, one: string, few: string, many: string): string {
  const lastTwo = value % 100;
  const last = value % 10;
  const noun = last === 1 && lastTwo !== 11
    ? one
    : last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14)
      ? few
      : many;
  return `${NUMBER_FORMAT.format(value)} ${noun}`;
}

export function ActivityPane() {
  const [period, setPeriod] = useState('7');
  const days = period.startsWith('m:') ? 31 : Number(period);
  const month = period.startsWith('m:') ? period.slice(2) : null;
  const [logStats, setLogStats] = useState<AccessLogStats | null>(null);
  const [userId, setUserId] = useState<number | null>(null);
  const [module, setModule] = useState<string | null>(null);
  const [writesOnly, setWritesOnly] = useState(false);
  const [rows, setRows] = useState<AccessLogRow[] | null>(null);
  const [summary, setSummary] = useState<AccessSummaryRow[] | null>(null);
  const [modules, setModules] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const filters = useMemo<AccessLogFilters>(
    () => ({ days, month, userId, module, writes: writesOnly }),
    [days, month, module, userId, writesOnly],
  );

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);

    Promise.all([listAccessLog(filters), accessLogSummary({ days, month })])
      .then(([logResult, summaryResult]) => {
        if (!active) return;
        setRows(logResult.rows);
        setSummary(summaryResult.rows);
        setModules(summaryResult.modules);
        setLogStats(summaryResult.stats);
      })
      .catch((caught) => {
        if (!active) return;
        setRows([]);
        setError(errorMessage(caught));
      });

    return () => { active = false; };
  }, [days, month, filters]);

  useFeather([rows?.length ?? -1, summary?.length ?? -1, sending, info]);

  const totals = useMemo(() => (summary ?? []).reduce(
    (result, row) => ({
      total: result.total + row.total,
      details: result.details + row.details,
      writes: result.writes + row.writes,
    }),
    { total: 0, details: 0, writes: 0 },
  ), [summary]);

  const stats: { label: string; value: number | string; note: string; icon: string }[] = [
    { label: 'Události', value: summary ? totals.total : '…', note: month ? `za ${MONTH_OPTIONS.find((item) => item.value === month)?.label ?? month}` : `za ${days === 1 ? 'dnešek' : `${days} dní`}`, icon: 'activity' },
    { label: 'Uživatelé', value: summary ? summary.length : '…', note: 's evidovaným přístupem', icon: 'users' },
    { label: 'Otevřené detaily', value: summary ? totals.details : '…', note: 'čtení konkrétní entity', icon: 'eye' },
    { label: 'Zápisy a exporty', value: summary ? totals.writes : '…', note: 'citlivé operace', icon: 'edit-3' },
  ];

  const filtersActive = userId !== null || module !== null || writesOnly;

  function clearFilters() {
    setUserId(null);
    setModule(null);
    setWritesOnly(false);
  }

  async function sendNow() {
    setSending(true);
    setInfo(null);
    setError(null);
    try {
      await sendAccessSummaryNow();
      setInfo('Souhrn byl odeslán do Mattermostu.');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="settings-activity-workspace">
      <section className="settings-activity-overview" aria-labelledby="activity-overview-title">
        <header className="settings-activity-overview-head">
          <div className="settings-activity-overview-copy">
            <span className="settings-activity-icon" aria-hidden="true">
              <span className="feather-icon"><i data-feather="shield"></i></span>
            </span>
            <div>
              <span className="settings-activity-eyebrow">Bezpečnost</span>
              <h2 id="activity-overview-title">Audit datových přístupů</h2>
              <p>Kontrola čtení a změn citlivých dat napříč Rondem, Affilmasterem a CRM.</p>
              <div className="settings-activity-policy" aria-label="Pravidla auditu">
                <span><i aria-hidden="true"></i> Retence {logStats?.retentionDays ?? 180} dní</span>
                <span>Úkoly a helpdesk se neevidují</span>
                {logStats && (
                  <span title={logStats.oldestAt ? `Nejstarší záznam ${new Date(logStats.oldestAt).toLocaleDateString('cs-CZ')}` : undefined}>
                    Uloženo {counted(logStats.rows, 'záznam', 'záznamy', 'záznamů')} · {formatBytes(logStats.bytes)} na disku
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="settings-activity-actions">
            <a className="settings-activity-button is-secondary" href={accessLogCsvUrl(filters)} download>
              <span className="feather-icon" aria-hidden="true"><i data-feather="download"></i></span>
              Exportovat CSV
            </a>
            <button type="button" className="settings-activity-button is-primary" disabled={sending} onClick={() => void sendNow()}>
              <span className="feather-icon" aria-hidden="true"><i data-feather="send"></i></span>
              {sending ? 'Odesílám…' : 'Poslat souhrn'}
            </button>
          </div>
        </header>

        <div className="settings-activity-stats">
          {stats.map((stat) => (
            <div className="settings-activity-stat" key={stat.label}>
              <span className="settings-activity-stat-icon" aria-hidden="true">
                <span className="feather-icon"><i data-feather={stat.icon}></i></span>
              </span>
              <div>
                <span>{stat.label}</span>
                <strong>{typeof stat.value === 'number' ? NUMBER_FORMAT.format(stat.value) : stat.value}</strong>
                <small>{stat.note}</small>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="settings-activity-log" aria-labelledby="activity-log-title">
        <header className="settings-activity-log-head">
          <div>
            <span className="settings-activity-eyebrow">Auditní stopa</span>
            <h2 id="activity-log-title">Poslední události</h2>
            <p>Filtry se promítají také do exportovaného CSV.</p>
          </div>
          <span className="settings-activity-result-count">
            {rows === null ? 'Načítám' : counted(rows.length, 'záznam', 'záznamy', 'záznamů')}
          </span>
        </header>

        <div className="settings-activity-filterbar" aria-label="Filtry auditní stopy">
          <label className="settings-activity-filter">
            <span>Období</span>
            <select value={period} onChange={(event) => setPeriod(event.target.value)}>
              <option value="1">Dnes</option>
              <option value="7">Posledních 7 dní</option>
              <option value="31">Posledních 31 dní</option>
              <option value="90">Posledních 90 dní</option>
              <optgroup label="Kalendářní měsíc">
                {MONTH_OPTIONS.map((item) => <option key={item.value} value={`m:${item.value}`}>{item.label}</option>)}
              </optgroup>
            </select>
          </label>
          <label className="settings-activity-filter">
            <span>Uživatel</span>
            <select value={userId ?? ''} onChange={(event) => setUserId(event.target.value ? Number(event.target.value) : null)}>
              <option value="">Všichni uživatelé</option>
              {(summary ?? []).map((user) => <option key={user.userId} value={user.userId}>{user.userName || user.email}</option>)}
            </select>
          </label>
          <label className="settings-activity-filter">
            <span>Modul</span>
            <select value={module ?? ''} onChange={(event) => setModule(event.target.value || null)}>
              <option value="">Všechny moduly</option>
              {modules.map((moduleName) => <option key={moduleName} value={moduleName}>{moduleLabel(moduleName)}</option>)}
            </select>
          </label>
          <label className="settings-activity-switch">
            <input type="checkbox" checked={writesOnly} onChange={(event) => setWritesOnly(event.target.checked)} />
            <span className="settings-activity-switch-control" aria-hidden="true"></span>
            <span>
              <strong>Jen citlivé akce</strong>
              <small>Zápisy a exporty</small>
            </span>
          </label>
          {filtersActive && (
            <button type="button" className="settings-activity-clear" onClick={clearFilters}>
              <span className="feather-icon" aria-hidden="true"><i data-feather="x"></i></span>
              Zrušit filtry
            </button>
          )}
        </div>

        {summary && summary.length > 0 && (
          <div className="settings-activity-people" aria-label="Souhrn podle uživatelů">
            <div className="settings-activity-people-label">
              <strong>Aktivní uživatelé</strong>
              <span>Výběrem rychle omezíte výpis</span>
            </div>
            <div className="settings-activity-people-list">
              {summary.map((user) => {
                const selected = userId === user.userId;
                return (
                  <button
                    type="button"
                    className={`settings-activity-person${selected ? ' is-selected' : ''}`}
                    key={user.userId}
                    aria-pressed={selected}
                    title={user.modules ? `Moduly: ${user.modules}` : undefined}
                    onClick={() => setUserId(selected ? null : user.userId)}
                  >
                    <span className="settings-activity-avatar" aria-hidden="true">{initials(user.userName, user.email)}</span>
                    <span className="settings-activity-person-copy">
                      <strong>{user.userName || user.email}</strong>
                      <small>{user.email}</small>
                    </span>
                    <span className="settings-activity-person-metrics">
                      <strong>{NUMBER_FORMAT.format(user.total)}</strong>
                      <small>{counted(user.details, 'detail', 'detaily', 'detailů')} · {counted(user.writes, 'zápis', 'zápisy', 'zápisů')}</small>
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {error && (
          <div className="settings-activity-message is-error" role="alert">
            <span className="feather-icon" aria-hidden="true"><i data-feather="alert-circle"></i></span>
            <div><strong>Data se nepodařilo načíst</strong><span>{error}</span></div>
          </div>
        )}
        {info && (
          <div className="settings-activity-message is-success" role="status">
            <span className="feather-icon" aria-hidden="true"><i data-feather="check-circle"></i></span>
            <div><strong>Hotovo</strong><span>{info}</span></div>
          </div>
        )}

        {rows === null ? (
          <div className="settings-activity-loading" aria-live="polite">
            <span></span><span></span><span></span>
            <p>Načítám auditní stopu…</p>
          </div>
        ) : rows.length === 0 && !error ? (
          <div className="settings-activity-empty">
            <span className="settings-activity-empty-icon" aria-hidden="true">
              <span className="feather-icon"><i data-feather="search"></i></span>
            </span>
            <h3>Žádné odpovídající události</h3>
            <p>Zkuste rozšířit období nebo zrušit některý z filtrů.</p>
            {filtersActive && <button type="button" onClick={clearFilters}>Zrušit filtry</button>}
          </div>
        ) : rows.length > 0 ? (
          <div className="settings-activity-table-scroll">
            <table className="settings-activity-table">
              <caption className="visually-hidden">Přístupy uživatelů k citlivým datům</caption>
              <thead>
                <tr>
                  <th>Čas</th>
                  <th>Uživatel</th>
                  <th>Modul</th>
                  <th>Akce</th>
                  <th>Cesta</th>
                  <th title="Výsledek požadavku">Výsledek</th>
                  <th>IP adresa</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const createdAt = new Date(row.createdAt);
                  const sensitive = row.action === 'write' || row.action === 'export';
                  return (
                    <tr key={row.id} className={sensitive ? 'is-sensitive' : undefined}>
                      <td>
                        <time className="settings-activity-time" dateTime={row.createdAt} title={FULL_DATE_FORMAT.format(createdAt)}>
                          <strong>{TIME_FORMAT.format(createdAt)}</strong>
                          <small>{SHORT_DATE_FORMAT.format(createdAt)}</small>
                        </time>
                      </td>
                      <td>
                        <div className="settings-activity-user">
                          <span aria-hidden="true">{initials(row.userName, row.email)}</span>
                          <div><strong>{row.userName || row.email}</strong><small>{row.email}</small></div>
                        </div>
                      </td>
                      <td><span className="settings-activity-module" title={row.moduleKey}>{moduleLabel(row.moduleKey)}</span></td>
                      <td><span className={`settings-activity-action is-${row.action}`} title={ACTION_TITLE[row.action]}>{ACTION_LABEL[row.action]}</span></td>
                      <td>
                        <div className="settings-activity-path">
                          <span>{row.method}</span>
                          <code>{visiblePath(row.path)}</code>
                          {row.entityId && <small>Entita {row.entityId}</small>}
                        </div>
                      </td>
                      <td><span className={`settings-activity-status is-${statusTone(row.statusCode)}`} title={`${statusLabel(row.statusCode)[1]} (HTTP ${row.statusCode})`}><i aria-hidden="true"></i>{statusLabel(row.statusCode)[0]}</span></td>
                      <td><code className="settings-activity-ip">{row.ip ?? 'neuvedena'}</code></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </div>
  );
}

